import { useState, useEffect } from "react";

const navLinks = [
  { label: "Home", href: "#home" },
  { label: "About Us", href: "#about" },
  { label: "Our Plans", href: "#plans" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState("home");

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      const sections = ["home", "about", "plans", "testimonials", "faq", "contact"];
      for (const id of [...sections].reverse()) {
        const el = document.getElementById(id);
        if (el && window.scrollY >= el.offsetTop - 100) {
          setActive(id);
          break;
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNav = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const id = href.replace("#", "");
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled ? "bg-white border-b border-emerald-100 py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <h1 className="flex items-center gap-2">
          <span
            className={`w-9 h-9 rounded-lg flex items-center justify-center ${
              scrolled ? "bg-emerald-700" : "bg-white/20 border border-white/40"
            }`}
          >
            <i className={`ri-heart-pulse-line text-lg ${scrolled ? "text-white" : "text-white"}`}></i>
          </span>
          <span
            className={`font-['Poppins'] font-700 text-lg leading-tight ${
              scrolled ? "text-emerald-900" : "text-white"
            }`}
          >
            HealthMarkets<br />
            <span className={`text-xs font-400 ${scrolled ? "text-emerald-600" : "text-white/80"}`}>
              Insurance Agency
            </span>
          </span>
        </h1>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => {
            const id = link.href.replace("#", "");
            return (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNav(e, link.href)}
                className={`px-4 py-2 rounded-lg text-sm font-['Open_Sans'] font-500 transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  active === id
                    ? scrolled
                      ? "bg-emerald-50 text-emerald-800 font-600"
                      : "bg-white/20 text-white font-600"
                    : scrolled
                    ? "text-gray-600 hover:text-emerald-800 hover:bg-emerald-50"
                    : "text-white/90 hover:text-white hover:bg-white/10"
                }`}
              >
                {link.label}
              </a>
            );
          })}
        </nav>

        {/* Right CTA */}
        <div className="hidden lg:flex items-center gap-3">
          <a
            href="tel:9722174662"
            className={`flex items-center gap-2 text-sm font-['Open_Sans'] font-600 whitespace-nowrap cursor-pointer transition-colors ${
              scrolled ? "text-emerald-800" : "text-white"
            }`}
          >
            <div className={`w-7 h-7 rounded-full flex items-center justify-center ${scrolled ? "bg-emerald-100" : "bg-white/20"}`}>
              <i className={`ri-phone-line text-sm ${scrolled ? "text-emerald-700" : "text-white"}`}></i>
            </div>
            972-217-4662
          </a>
          <a
            href="#plans"
            onClick={(e) => handleNav(e, "#plans")}
            className="px-5 py-2.5 rounded-lg bg-amber-500 text-white text-sm font-['Poppins'] font-600 hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Get a Quote
          </a>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className="lg:hidden cursor-pointer p-2 rounded-lg"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-2xl ${scrolled ? "text-emerald-900" : "text-white"} ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="lg:hidden bg-white border-t border-emerald-100 px-6 py-4">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNav(e, link.href)}
              className="block py-3 border-b border-gray-100 text-gray-700 font-['Open_Sans'] text-sm cursor-pointer hover:text-emerald-700"
            >
              {link.label}
            </a>
          ))}
          <a href="tel:9722174662" className="mt-4 flex items-center gap-2 text-emerald-800 font-600 text-sm cursor-pointer">
            <i className="ri-phone-line"></i> 972-217-4662
          </a>
        </div>
      )}
    </header>
  );
}
